import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from './utils/Toast';
import Loader from './utils/Loader';
import Navbar from './utils/Navbr';
import './style/Profile.css';

const Profile = () => {
    const navigate = useNavigate();

    // --- State ---
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [loginHistory, setLoginHistory] = useState([]);
    
    // Forms
    const [telegramId, setTelegramId] = useState('');
    const [passData, setPassData] = useState({ old: '', new: '', confirm: '' });
    
    // Loading States for buttons
    const [tgLoading, setTgLoading] = useState(false);
    const [passLoading, setPassLoading] = useState(false);

    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    };

    // --- Fetch Data ---
    useEffect(() => {
        const authToken = getCookie('authToken');
        const fetchData = async () => {
            try {
                setLoading(true);

                // 1. Auth & Profile
                const authRes = await axios.post('https://publisher.cashbolt.in.net/server/', { 
                    action: 'checkAuth', 
                    authToken 
                });

                if (!authRes.data.status) {
                    navigate('/login');
                    return;
                }
                
                const userData = authRes.data.res;
                setUser(userData);
                setTelegramId(userData.tgId || ''); // Pre-fill if exists

                // 2. Login History
                const historyRes = await axios.post('https://publisher.cashbolt.in.net/server/', {
                    action: 'getLoginHistory',
                    authToken
                });

                if (historyRes.data.status) {
                    setLoginHistory(historyRes.data.history || []);
                }

            } catch (error) {
                toast.error('Failed to load profile');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [navigate]);

    // --- Actions ---
    const handleUpdateTelegram = async () => {
        if (!telegramId.trim()) return toast.warning('Please enter a valid Telegram ID');
        
        setTgLoading(true);
        const authToken = getCookie('authToken');
        try {
            const res = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'updateTelegram',
                authToken,
                telegramId
            });
            if (res.data.status) {
                toast.success('Telegram ID Updated!');
            } else {
                toast.error(res.data.message || 'Update failed');
            }
        } catch (err) {
            toast.error('Network Error');
        } finally {
            setTgLoading(false);
        }
    };

    const handleChangePassword = async () => {
        if (!passData.old || !passData.new || !passData.confirm) {
            return toast.warning('All fields are required');
        }
        if (passData.new !== passData.confirm) {
            return toast.warning('New passwords do not match');
        }

        setPassLoading(true);
        const authToken = getCookie('authToken');
        try {
            const res = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'changePassword',
                authToken,
                oldPassword: passData.old,
                newPassword: passData.new
            });
            if (res.data.status) {
                toast.success('Password Changed Successfully!');
                setPassData({ old: '', new: '', confirm: '' });
            } else {
                toast.error(res.data.message || 'Change failed');
            }
        } catch (err) {
            toast.error('Network Error');
        } finally {
            setPassLoading(false);
        }
    };

    // Helper: Get Initials
    const getInitials = (name) => {
        if (!name) return 'U';
        const parts = name.split(' ');
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    };

    if (loading) return <Loader />;

    return (
        <div className="profile-page-wrapper-unique">
            <div className="bg-shape-pro shape-p1"></div>
            <div className="bg-shape-pro shape-p2"></div>
            <Navbar name={user?.name} role={user?.role} />

            <div className="profile-container-unique">
                
                {/* 1. Publisher Details Card */}
                <div className="profile-card-unique main-info-card">
                    <div className="avatar-section">
                        <div className="big-avatar-pulse">
                            {getInitials(user?.name)}
                            <div className="pulse-ring"></div>
                        </div>
                        <h2 className="profile-name">{user?.name}</h2>
                        <span className="profile-role-badge">{user?.role || 'Publisher'}</span>
                    </div>

                    <div className="info-grid-unique">
                        <div className="info-item">
                            <label>Email Address</label>
                            <div className="info-val">{user?.email}</div>
                        </div>
                        <div className="info-item">
                            <label>Phone Number</label>
                            <div className="info-val">{user?.number || 'N/A'}</div>
                        </div>
                        <div className="info-item">
                            <label>Company</label>
                            <div className="info-val">{user?.compName || 'Independent'}</div>
                        </div>
                        <div className="info-item">
                            <label>Current Balance</label>
                            <div className="info-val highlight-rupee">₹{parseFloat(user?.balance || 0).toLocaleString('en-IN')}</div>
                        </div>
                        <div className="info-item">
                            <label>Joined On</label>
                            <div className="info-val">{new Date(user?.createdAt || Date.now()).toDateString()}</div>
                        </div>
                    </div>
                </div>

                <div className="profile-split-row">
                    
                    {/* 2. Telegram Update */}
                    <div className="profile-card-unique split-card">
                        <h3 className="card-heading-unique">Telegram Contact</h3>
                        <div className="form-stack">
                            <div className="input-group-pro">
                                <span className="input-icon">✈️</span>
                                <input 
                                    type="text" 
                                    placeholder="543456775" 
                                    value={telegramId}
                                    onChange={(e) => setTelegramId(e.target.value)}
                                />
                            </div>
                            <button className="submit-btn-black-wave" onClick={handleUpdateTelegram} disabled={tgLoading}>
                                {tgLoading ? 'Updating...' : 'Update Telegram ID'}
                            </button>
                        </div>
                    </div>

                    {/* 3. Change Password */}
                    <div className="profile-card-unique split-card">
                        <h3 className="card-heading-unique">Security</h3>
                        <div className="form-stack">
                            <div className="input-group-pro">
                                <span className="input-icon">🔒</span>
                                <input 
                                    type="password" 
                                    placeholder="Current Password" 
                                    value={passData.old}
                                    onChange={(e) => setPassData({...passData, old: e.target.value})}
                                />
                            </div>
                            <div className="input-group-pro">
                                <span className="input-icon">🔑</span>
                                <input 
                                    type="password" 
                                    placeholder="New Password" 
                                    value={passData.new}
                                    onChange={(e) => setPassData({...passData, new: e.target.value})}
                                />
                            </div>
                            <div className="input-group-pro">
                                <span className="input-icon">✅</span>
                                <input 
                                    type="password" 
                                    placeholder="Confirm New Password" 
                                    value={passData.confirm}
                                    onChange={(e) => setPassData({...passData, confirm: e.target.value})}
                                />
                            </div>
                            <button className="submit-btn-black-wave" onClick={handleChangePassword} disabled={passLoading}>
                                {passLoading ? 'Changing...' : 'Change Password'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* 4. Login History */}
                <div className="profile-card-unique history-card">
                    <h3 className="card-heading-unique">Login History</h3>
                    <div className="history-list-container">
                        {loginHistory.length > 0 ? (
                            loginHistory.map((log) => (
                                <div key={log.id} className="history-row-unique">
                                    <div className="history-left">
                                        <div className="device-name">
                                            <span className="device-icon">💻</span> {log.device}
                                        </div>
                                        <div className="login-time">{log.time}</div>
                                    </div>
                                    <div className="history-right">
                                        <div className="location-badge">
                                            📍 {log.location}
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-history">No login history found.</div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Profile;